import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, CalendarCheck, Loader2, Mail, Phone } from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api, formatApiError } from "../lib/api";
import { useContent } from "../lib/content";
import { TIME_SLOTS } from "../data/services";

const INITIAL = { name: "", email: "", phone: "", business: "", service: "", preferred_date: "", preferred_time: "", message: "" };

const inputClass =
  "w-full border-b border-forest/25 bg-transparent py-3 text-base text-forest placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-olive";

export default function Contact() {
  const { content } = useContent();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    api
      .get("/availability/blocked")
      .then((r) => setBlockedDates(r.data))
      .catch(() => {});
  }, []);

  const dateBlocked = !!form.preferred_date && blockedDates.includes(form.preferred_date);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const svc = searchParams.get("service");
    if (svc) setForm((f) => ({ ...f, service: svc }));
  }, [searchParams]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const phoneHref = `tel:+1${content.contact.phoneDisplay.replace(/\D/g, "")}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (dateBlocked) {
      toast.error("That date is unavailable — please choose another day.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consultations", form);
      toast.success("Request sent! Nothing is booked yet — Jennifer will personally confirm your requested time by email.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Request a Consultation | Rooted Business Advisors"
        description="Request a consultation with Rooted Business Advisors — bookkeeping, payroll oversight and financial guidance for Greater Cincinnati and Southeast Indiana small businesses."
      />
      <section className="bg-forest-deep pb-24 pt-44 text-cream">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Eyebrow light>Request a Consultation</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl" data-testid="contact-headline">
            <MaskedLine delay={0.15}>{content.contact.line1}</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-gold">
              {content.contact.line2}
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl leading-relaxed text-cream/75 md:text-lg">{content.contact.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="flex h-full flex-col justify-between gap-12 bg-forest p-10 text-cream">
              <div>
                <Eyebrow light>Reach Us Directly</Eyebrow>
                <div className="mt-10 space-y-8">
                  <a href={phoneHref} data-testid="contact-phone-link" className="group block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">Phone</span>
                    <span className="mt-2 flex items-center gap-3 font-display text-2xl transition-colors duration-300 group-hover:text-gold">
                      <Phone className="h-5 w-5 text-gold" strokeWidth={1.5} />
                      {content.contact.phoneDisplay}
                    </span>
                  </a>
                  <a href={`mailto:${content.contact.email}`} data-testid="contact-email-link" className="group block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">Email</span>
                    <span className="mt-2 flex items-center gap-3 break-all text-sm transition-colors duration-300 group-hover:text-gold">
                      <Mail className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                      {content.contact.email}
                    </span>
                  </a>
                </div>
              </div>
              <p className="border-t border-cream/15 pt-8 font-display text-xl italic leading-snug text-cream/70">
                "Strong roots make for confident growth."
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-8">
            <form onSubmit={onSubmit} className="grid gap-10 sm:grid-cols-2" data-testid="consultation-form">
              <div>
                <label htmlFor="cf-name" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Your Name *
                </label>
                <input id="cf-name" data-testid="consultation-name-input" required value={form.name} onChange={set("name")} className={inputClass} placeholder="Jane Smith" />
              </div>
              <div>
                <label htmlFor="cf-email" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Email *
                </label>
                <input id="cf-email" type="email" data-testid="consultation-email-input" required value={form.email} onChange={set("email")} className={inputClass} placeholder="jane@yourbusiness.com" />
              </div>
              <div>
                <label htmlFor="cf-phone" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Phone
                </label>
                <input id="cf-phone" data-testid="consultation-phone-input" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="(513) 555-0100" />
              </div>
              <div>
                <label htmlFor="cf-business" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Business Name
                </label>
                <input id="cf-business" data-testid="consultation-business-input" value={form.business} onChange={set("business")} className={inputClass} placeholder="Your Business, LLC" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-service" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Service You're Interested In
                </label>
                <select id="cf-service" data-testid="consultation-service-select" value={form.service} onChange={set("service")} className={`${inputClass} cursor-pointer`}>
                  <option value="">Not sure yet — help me decide</option>
                  {content.services.map((s) => (
                    <option key={s.id || s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cf-date" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Preferred Date
                </label>
                <input id="cf-date" type="date" data-testid="consultation-date-input" value={form.preferred_date} onChange={set("preferred_date")} min={new Date().toISOString().split("T")[0]} className={`${inputClass} cursor-pointer`} />
                {dateBlocked && (
                  <p className="mt-2 text-xs font-semibold text-red-700" data-testid="date-blocked-warning">
                    That day is unavailable — please choose another date.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="cf-time" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  Preferred Time
                </label>
                <select id="cf-time" data-testid="consultation-time-select" value={form.preferred_time} onChange={set("preferred_time")} className={`${inputClass} cursor-pointer`}>
                  <option value="">Any time works</option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 -mt-4 flex items-start gap-2 text-xs leading-relaxed text-sage" data-testid="booking-note">
                <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-olive" strokeWidth={1.5} />
                This is a request, not a confirmed booking — Jennifer personally reviews and confirms every appointment time by email.
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                  How Can We Help? *
                </label>
                <textarea id="cf-message" data-testid="consultation-message-input" required rows={4} value={form.message} onChange={set("message")} className={`${inputClass} resize-none`} placeholder="Tell us about your business and what you're looking for…" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="consultation-submit-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      Sending
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Consultation Request
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
